import React, { useState, useMemo } from 'react';
import { Input, Table } from 'antd-v5';
import { SearchOutlined } from '@ant-design/icons';
import useGetClients from '../../../../hooks/query/useGetClients';
import useDebounce from '../../../../hooks/useDebounce';
import { getAvatarColor } from '../../../../utils/getAvatarColor';
import formatPhoneNumber from '../../../../utils/formatPhoneNumber';

const getInitials = name => {
  return (name || '')
    .split(' ')
    .map(e => e.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    render: name => {
      const initials = getInitials(name);
      const avatarColor = getAvatarColor(name);
      return (
        <span>
          <span className="merge-search-result-avatar" style={{ background: avatarColor.bg }}>
            {initials}
          </span>
          {name}
        </span>
      );
    },
  },
  {
    title: 'POS ID',
    dataIndex: 'referenceNumber',
    key: 'referenceNumber',
    render: val => (val ? <span className="merge-search-secondary-text">{val}</span> : null),
  },
  {
    title: 'Mobile',
    dataIndex: 'mobile',
    key: 'mobile',
    render: val => (val ? <span className="merge-search-secondary-text">{formatPhoneNumber(val, 'b')}</span> : null),
  },
];

const MergeSearchStep = ({ sourceClientId, onSelect }) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const { data: { data: results } = { data: [] }, isFetching } = useGetClients({
    data: {
      searchTerm: debouncedSearch,
      offset: 0,
      limit: 10,
      storeId: 0,
      archived: false,
    },
    loadingStatusOff: true,
    useQueryOptions: {
      enabled: debouncedSearch.length > 0,
    },
  });

  const numericSourceId = Number(sourceClientId);
  const filteredResults = useMemo(
    () => (results || []).filter(client => Number(client.id) !== numericSourceId),
    [results, numericSourceId],
  );

  const hasSearched = debouncedSearch.length > 0;

  return (
    <div>
      <h2 className="merge-modal-title">Merge with Another Client</h2>
      <p className="merge-modal-description">
        Search for a duplicate client to merge into this one. The duplicate will be archived, and their information will
        be merged into this client profile. You can choose which fields to keep.
      </p>

      <Input
        className="merge-search-input"
        size="large"
        placeholder="Find a Client"
        prefix={<SearchOutlined />}
        value={search}
        onChange={e => setSearch(e.target.value)}
        allowClear
      />

      {hasSearched && (
        <div className="merge-search-results">
          <Table
            columns={columns}
            dataSource={filteredResults}
            rowKey="id"
            size="small"
            loading={isFetching}
            pagination={false}
            scroll={{ x: true }}
            locale={{ emptyText: isFetching ? ' ' : 'No clients found' }}
            onRow={record => ({
              onClick: () => onSelect(record),
            })}
          />
        </div>
      )}
    </div>
  );
};

export default MergeSearchStep;
